import { useCallback, useEffect, useState } from "react";
import { type PatreonLinkState, userApi } from "./api";
import { useSession } from "./session";

/**
 * Premium via Patreon, client side: one hook for the two surfaces that show the link
 * (the Gold dialog and the profile card), and the three actions on it.
 *
 * `state` is null until the server answers; a box with no Patreon configured answers
 * `enabled: false`. SIGNED OUT GETS THE STATE TOO (2026-09-10): the link is null but
 * enabled and the price are public facts, and without them the dialog showed a
 * signed-out visitor — most of the people who ever open it — the old pass ladder
 * instead of the membership. Re-read on sign-in so the link lands.
 */
export type PatreonRefreshTrigger = "return" | "poll" | "manual";

export function usePatreonLink(): {
  state: PatreonLinkState | null;
  busy: boolean;
  connect: (source: string) => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * The same ask, without touching `busy`. The panel calls this on its own clock —
   * when the tab comes back from Patreon and every ten seconds after a Subscribe click
   * — and a `busy` flip on each tick would disable both buttons and swap the Refresh
   * label to "Asking Patreon…" under the person's eyes. Failures are swallowed the same
   * way: the card keeps what it had.
   */
  refreshQuiet: (trigger: PatreonRefreshTrigger) => Promise<void>;
  disconnect: () => Promise<void>;
} {
  const user = useSession((s) => s.user);
  const refreshSession = useSession((s) => s.refresh);
  const [state, setState] = useState<PatreonLinkState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    userApi
      .patreonLink()
      .then((s) => live && setState(s))
      .catch(() => live && setState(null));
    return () => {
      live = false;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Off to Patreon's consent page; the callback lands on /gold/thanks. The click itself
   * is the panel's to report (PatreonPremium) — it knows which surface it sits in.
   */
  const connect = useCallback(async (source: string) => {
    setBusy(true);
    try {
      const { url } = await userApi.patreonConnect(source);
      window.location.href = url;
    } catch {
      setBusy(false);
    }
  }, []);

  // A refresh may have moved gold_until on the server while this tab sat still — the
  // same reason the thanks page re-reads /auth/me — so the session is re-read too.
  const refreshQuiet = useCallback(
    async (trigger: PatreonRefreshTrigger) => {
      try {
        setState(await userApi.patreonRefresh(trigger));
        const { user: fresh } = await userApi.me();
        refreshSession(fresh);
      } catch {
        /* the card keeps what it had */
      }
    },
    [refreshSession],
  );

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      await refreshQuiet("manual");
    } finally {
      setBusy(false);
    }
  }, [refreshQuiet]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    try {
      setState(await userApi.patreonUnlink());
    } catch {
      /* ditto */
    } finally {
      setBusy(false);
    }
  }, []);

  return { state, busy, connect, refresh, refreshQuiet, disconnect };
}
